/**
 * 公開コメント・いいねのクライアント（EP-1 公開データ・M6c）。
 *
 * 個人データ（`user-data.ts`）と違い、ここで扱うのは**他人にも見えるデータ**なので
 * localStorage への控えは持たない（承認前の本文を端末に残しても意味がない）。
 * 失敗は例外にせず縮退する — コメントが出ないことより、読書が止まらないことを優先する。
 *
 * 本文はサーバに生のまま保存され、表示側が `textContent` で描く（HTML として解釈しない）。
 */

/** 承認済みコメント 1 件（外部 ID はサーバから返らない）。 */
export interface CommentItem {
  id: number;
  /** 投稿時点の表示名。導入側が渡していなければ null。 */
  name: string | null;
  body: string;
  /** 投稿時刻（unix 秒）。 */
  createdAt: number;
}

/** 投稿結果。`pending` なら承認されるまで公開されない。 */
export interface PostResult {
  id: number;
  status: 'pending' | 'approved';
}

/** いいねの状態（`liked` は現在の閲覧者の分）。 */
export interface LikeState {
  count: number;
  liked: boolean;
}

export class Comments {
  /**
   * @param base API の基準（例 `/api/v1`）。
   * @param slug 対象ブック。
   */
  constructor(
    private readonly base: string,
    private readonly slug: string,
  ) {}

  /** 承認済みコメント（投稿順）。読み取りは未ログインでもできる。 */
  async list(signal?: AbortSignal): Promise<CommentItem[]> {
    const data = await this.request<{ items?: CommentItem[] }>('comments', 'GET', undefined, signal);
    return data?.items ?? [];
  }

  /** 投稿する（要ログイン）。失敗（未ログイン・連投・上限超過）は null。 */
  post(body: string): Promise<PostResult | null> {
    return this.request<PostResult>('comments', 'POST', { body });
  }

  likeState(signal?: AbortSignal): Promise<LikeState | null> {
    return this.request<LikeState>('like', 'GET', undefined, signal);
  }

  /** いいねを付ける・外す（要ログイン）。付ける操作は冪等。 */
  setLike(on: boolean): Promise<LikeState | null> {
    return this.request<LikeState>('like', on ? 'PUT' : 'DELETE');
  }

  private async request<T>(
    resource: 'comments' | 'like',
    method: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T | null> {
    const url = `${this.base}/books/${encodeURIComponent(this.slug)}/${resource}`;
    try {
      const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        ...(signal ? { signal } : {}),
        ...(body === undefined
          ? {}
          : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
      });
      if (!response.ok) return null;
      return (await response.json()) as T;
    } catch {
      return null; // 中断・ネットワーク断でも読書を止めない
    }
  }
}
